import { reportError } from "../lib/ybug";

/**
 * Klient panelu konsultanta.
 *
 * Dwie drogi wejścia, bo to dwa różne rodzaje wywołujących. Klucz `X-Admin-Key`
 * jest dla maszyn i dla awarii — nie wygasa i nie mówi, kto go użył. Sesja
 * z logowania Microsoftem niesie adres e-mail, więc w dzienniku zostaje człowiek.
 * Gdy jest sesja, klucza nie wysyłamy: inaczej ślad znów byłby anonimowy.
 */

const KEY_STORAGE = "kyc.admin-key";
const SESSION_STORAGE = "kyc.admin-session";

export interface AdminStats {
  total: number;
  open: number;
  completed: number;
  archived: number;
  last_7_days: number;
  blocked: number;
  completion_rate: number;
  delta: Record<"started" | "completed", { value: number; change: number; percent: number; direction: "up" | "down" | "flat" }>;
  by_stage: { stage: string; label: string; count: number }[];
  stuck_on: { key: string; count: number }[];
}

export interface RecordRow {
  record_id: string;
  /** Numer sprawy w systemie zlecającego — tym numerem szuka konsultant. */
  external_id: string | null;
  phone: string;
  name: string | null;
  schema_id: string;
  status: string;
  /** Co robi kandydat: nie zaczął, wypełnia, skończył, zamknięte. */
  master: string;
  master_label: string;
  /** Czy prosił o kontakt i czy go dostał. */
  help_status: string;
  help_label: string;
  /** Wynik sprawy: `success` (kandydat doszedł do końca) albo `closed` (z panelu). */
  outcome: string | null;
  needs_help: boolean;
  help_requested_at: string | null;
  progress: number;
  answers: number;
  verified: boolean;
  created_at: string | null;
  updated_at: string | null;
  magic_link: string;
}

export interface AuditRow {
  ts: string;
  action: string;
  field: string;
  /** Kto to zrobił: adres konsultanta, „panel" przy kluczu, pusto przy kandydacie. */
  actor: string | null;
  origin: string | null;
  detail: string | null;
  /** Kto stoi za wpisem: `candidate`, `consultant`, `api`, `document`, `system`. */
  source?: string;
}

export interface StoredFileRow {
  id: string;
  key: string;
  /** Nazwa pytania, do którego plik należy — po niej grupujemy kafelki. */
  title: string;
  /** Numer z wydruku — mają go dokumenty, które wystawiliśmy my, nie skany kandydata. */
  doc_number: string | null;
  filename: string;
  size_kb: number;
  content_type: string;
  created_at: string | null;
}

export interface DatapointRow {
  key: string;
  index: number;
  datapoint_type: string;
  stage: string | null;
  ui_params: Record<string, Record<string, string>>;
  llm_params: Record<string, unknown>;
  options: { id: string; label: Record<string, string> }[] | null;
  validation: Record<string, unknown> | null;
  conditions: unknown;
  outcome_def: unknown;
  default_value: string | null;
  severity: string | null;
  blocking: boolean;
  extraction_targets: string[] | null;
  optional: boolean;
  /** Pozostałość po usuniętej weryfikacji — nie używana, ale zachowana w schemacie. */
  risk: string | null;
  risk_values: string[] | null;
  /** Wymagania wobec pliku: ile, w jakim formacie, kiedy przyjąć. */
  file_params: {
    min?: number;
    max?: number;
    formats?: string[];
    accept_when?: string;
  } | null;
  /** Tylko `set_flag`: flaga zapisywana w sprawie, gdy ścieżka dojdzie do węzła. */
  flag: { id: string; value: string } | null;
  /** Tylko `idenfy-*`: odczyt z dokumentu → klucz datapointu, do którego trafia. */
  value_mapping: Record<string, string> | null;
}

/** Typy iDenfy i predefiniowane pola, które każdy z nich odczytuje. */
export type KycTypes = Record<string, {
  label: string; doc: string; polish: boolean; fields: { id: string; label: string }[];
}>;

export interface MessageRow {
  record_id: string;
  channel: "sms" | "email";
  kind: string;
  to: string;
  provider: string;
  delivered: boolean;
  error: string | null;
  created_at: string | null;
}

export interface Finding {
  code: string;
  severity: "high" | "medium" | "low";
  count: number;
  title: string;
  cause: string;
  action: string;
  sample_ids: string[];
}

export interface AutocheckRun {
  check_id: string;
  title: string;
  status: "never" | "running" | "ok" | "findings" | "error";
  trigger: string | null;
  started_at: string | null;
  finished_at: string | null;
  findings: Finding[];
  error: string | null;
}

/** Dostęp do panelu: adres i rola. `superadmin` dodatkowo nadaje dostępy. */
export interface PanelUserRow {
  email: string;
  role: string;
  added_by: string | null;
  created_at: string | null;
  last_login_at: string | null;
}

/** Webhook z panelu: zdarzenie, opcjonalnie proces i pytanie, adres i wynik ostatniej dostawy. */
export interface WebhookRow {
  id: string;
  event: string;
  event_label: string;
  schema_id: string | null;
  datapoint_key: string | null;
  url: string;
  active: boolean;
  include_value: boolean;
  created_by: string | null;
  created_at: string | null;
  last_status: "ok" | "error" | null;
  last_detail: string | null;
  last_at: string | null;
}

/** Co można wybrać w formularzu webhooka. Lista zdarzeń jest zamknięta. */
export interface WebhookCatalog {
  events: { id: string; label: string; datapoint: boolean }[];
  processes: {
    schema_id: string;
    title: string;
    datapoints: { key: string; title: string; type: string }[];
  }[];
  statuses: Record<string, string>;
}

/** Podmiany tekstów dokumentu wbudowanego (`kind === "override"`). */
export interface TextOverride {
  title?: Record<string, string>;
  intro?: Record<string, string>;
  closing?: Record<string, string>;
  /** Klucz punktu (źródło odpowiedzi) → język → tekst. */
  claims?: Record<string, Record<string, string>>;
}

/** Domyślna treść dokumentu wbudowanego — z niej rysuje się edytor podmian. */
export interface BundledDoc {
  template: string;
  title: Record<string, string>;
  intro: Record<string, string>;
  closing: Record<string, string>;
  claims: { key: string; text: Record<string, string> }[];
}

/** Treść dokumentu do podpisu: język → tekst. Polski jest wiążący.
 *  `kind`: „custom" = własna treść (`title`/`body`), „override" = podmiana
 *  tekstów wbudowanego (`overrides`). Brak wpisu = szablon wbudowany bez zmian. */
export interface TemplateRow {
  datapoint_key: string;
  kind?: "custom" | "override";
  title: Record<string, string>;
  body: Record<string, string>;
  overrides?: TextOverride;
  updated_at?: string | null;
}

/** Kontrast koloru w roli, w której stoi — plakietka przy polu w edytorze szablonu. */
export interface BrandCheck {
  color: string;
  ratio: number | null;
  ok: boolean;
  label: string;
}

/** Wyjaśnienie systemu przy kolorach: co przesunął i dlaczego, z propozycją poprawki. */
export interface BrandNote {
  kind: "warn" | "info";
  color: string;
  text: string;
  suggest?: string;
}

export type BrandTokens = { light: Record<string, string>; dark: Record<string, string> };

/** Teksty marki: slot (powitanie/finał) → pole (tytuł/tekst) → język → tekst. */
export type BrandTexts = Partial<
  Record<"welcome" | "final" | "fail", Partial<Record<"title" | "text", Partial<Record<string, string>>>>>
>;

/** Szablon marki: skórka procesu kandydata (kolory, logo, powitanie, finał). */
export interface BrandRow {
  key: string;
  name: string;
  /** Wbudowany w kod (classic, city-drive, udriver). */
  builtin: boolean;
  /** Wbudowany z poprawkami zapisanymi w panelu — usunięcie przywraca kod. */
  overridden: boolean;
  /** Classic się nie edytuje: to wygląd for apps prosto z tokens.css. */
  editable: boolean;
  domains: string[];
  colors: Record<string, string>;
  texts: BrandTexts;
  schemas: string[];
  logo: { light: string | null; dark: string | null } | null;
  updated_at: string | null;
  updated_by: string | null;
  tokens: BrandTokens;
  checks: BrandCheck[];
  notes: BrandNote[];
  /** Sprawy, które podały ten klucz wprost w `theme`. */
  records: number;
}

export interface BrandPayload {
  name: string;
  domains: string[];
  colors: Record<string, string>;
  texts: BrandTexts;
  schemas: string[];
  /** data-URI nowego pliku · null = bez własnego logo · brak pola = bez zmian. */
  logo_light?: string | null;
  logo_dark?: string | null;
}

export class AdminError extends Error {
  status: number;
  problems: string[];

  constructor(status: number, message: string, problems: string[] = []) {
    super(message);
    this.status = status;
    this.problems = problems;
  }
}

export function readKey(): string {
  try {
    return localStorage.getItem(KEY_STORAGE) ?? "";
  } catch {
    return "";
  }
}

export function storeKey(key: string): void {
  try {
    localStorage.setItem(KEY_STORAGE, key);
  } catch {
    /* prywatne okno — panel zadziała do przeładowania */
  }
}

export function forgetKey(): void {
  try {
    localStorage.removeItem(KEY_STORAGE);
    localStorage.removeItem(SESSION_STORAGE);
  } catch {
    /* jw. */
  }
}

export function readSession(): string {
  try {
    return localStorage.getItem(SESSION_STORAGE) ?? "";
  } catch {
    return "";
  }
}

export function storeSession(token: string): void {
  try {
    localStorage.setItem(SESSION_STORAGE, token);
  } catch {
    /* prywatne okno — panel zadziała do przeładowania */
  }
}

/** Adres zalogowanego konsultanta — token niesie go jawnie, podpis chroni całość. */
export function sessionEmail(): string {
  const token = readSession();
  if (!token) return "";
  try {
    const [kto] = token.split(".");
    return atob(kto.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((kto.length + 3) % 4));
  } catch {
    return "";
  }
}

function authorise(headers: Headers): void {
  const session = readSession();
  if (session) headers.set("Authorization", `Bearer ${session}`);
  else headers.set("X-Admin-Key", readKey());
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  authorise(headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    reportError("admin_api", { status: 0, method: init.method ?? "GET", path, code: "offline" });
    throw new AdminError(0, "Brak połączenia z serwerem.");
  }

  const raw = await response.text();
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const detail = (parsed as { detail?: { code?: string; message?: string; problems?: string[] } } | null)?.detail;
    reportError("admin_api", { status: response.status, method: init.method ?? "GET", path, code: detail?.code });
    throw new AdminError(
      response.status,
      detail?.message ?? `Błąd ${response.status}`,
      detail?.problems ?? [],
    );
  }
  return parsed as T;
}

/**
 * Pliki panelu chodzą przez fetch, nie przez zwykły `<a href>`.
 *
 * Autoryzacja opiera się na nagłówku `X-Admin-Key`, a przeglądarka nie dokłada
 * nagłówków do zwykłej nawigacji — link otwarty w nowej karcie dostawał 401.
 * Pobieramy więc treść żądaniem i podajemy ją dalej jako blob.
 */
export async function fetchFile(path: string, init: RequestInit = {}): Promise<Blob> {
  const headers = new Headers(init.headers);
  authorise(headers);
  if (init.body) headers.set("Content-Type", "application/json");

  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    const detail = await response.text();
    throw new AdminError(response.status, response.status === 401
      ? "Klucz dostępu wygasł — zaloguj się ponownie."
      : `Nie udało się pobrać pliku (${response.status}). ${detail.slice(0, 120)}`);
  }
  return response.blob();
}

/** Otwiera plik w nowej karcie (podgląd PDF-a, skanu). */
export async function openFile(path: string, init?: RequestInit): Promise<void> {
  const url = URL.createObjectURL(await fetchFile(path, init));
  const tab = window.open(url, "_blank");
  if (!tab) {
    // Blokada wyskakujących okien — wtedy po prostu pobieramy.
    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Zapisuje plik na dysk pod podaną nazwą. */
export async function saveFile(path: string, filename: string, init?: RequestInit): Promise<void> {
  const url = URL.createObjectURL(await fetchFile(path, init));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const admin = {
  stats: () => call<AdminStats>("/api/admin/stats"),

  records: (params: {
    status?: string;
    search?: string;
    schemaId?: string;
    needsHelp?: boolean;
    master?: string;
    helpStatus?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    if (params.schemaId) query.set("schema_id", params.schemaId);
    if (params.needsHelp) query.set("needs_help", "true");
    if (params.master) query.set("master", params.master);
    if (params.helpStatus) query.set("help_status", params.helpStatus);
    query.set("limit", String(params.limit ?? 25));
    query.set("offset", String(params.offset ?? 0));
    return call<{ total: number; limit: number; offset: number; items: RecordRow[] }>(
      `/api/admin/records?${query}`,
    );
  },

  record: (id: string) => call<Record<string, any>>(`/api/admin/records/${id}`),
  audit: (id: string) => call<AuditRow[]>(`/api/admin/records/${id}/audit`),
  reset: (id: string) => call<{ status: string }>(`/api/admin/records/${id}/reset`, { method: "POST" }),

  // Wynik sprawy jedzie osobno od jej stanu: „zamknięte" nie mówi, czy
  // skończyło się umową, czy odmową.
  close: (id: string, outcome?: "success" | "failed") =>
    call<{ status: string; outcome: string | null }>(
      `/api/admin/records/${id}/close${outcome ? `?outcome=${outcome}` : ""}`,
      { method: "POST" },
    ),

  // Pomija dokładnie to pytanie, na którym stoi proces — konsultant nie musi
  // znać nazwy pola, a przez pomyłkę nie pominie innego.
  skip: (id: string) =>
    call<{ key: string; cleared: string[] }>(`/api/admin/records/${id}/skip`, { method: "POST" }),

  // Kto puka i co mu wolno — panel chowa po tym zarządzanie dostępami.
  me: () => call<{ actor: string; role: string | null }>("/api/admin/me"),

  users: () =>
    call<{ items: PanelUserRow[]; from_env: string[]; roles: string[]; you: string }>(
      "/api/admin/users",
    ),

  // Dodanie i zmiana roli to jedno wywołanie: dopisanie istniejącego adresu
  // zmienia mu rolę, zamiast kasować dostęp i nadawać go od nowa.
  addUser: (email: string, role: string) =>
    call<{ email: string; role: string }>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  removeUser: (email: string) =>
    call<{ deleted: string }>(`/api/admin/users/${encodeURIComponent(email)}`, {
      method: "DELETE",
    }),

  webhooks: () => call<{ items: WebhookRow[] }>("/api/admin/webhooks"),

  webhookCatalog: () => call<WebhookCatalog>("/api/admin/webhooks/catalog"),

  addWebhook: (body: {
    event: string;
    url: string;
    schema_id?: string | null;
    datapoint_key?: string | null;
    include_value?: boolean;
  }) =>
    call<WebhookRow>("/api/admin/webhooks", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateWebhook: (id: string, zmiany: { active?: boolean; include_value?: boolean }) =>
    call<WebhookRow>(`/api/admin/webhooks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(zmiany),
    }),

  deleteWebhook: (id: string) =>
    call<{ deleted: string }>(`/api/admin/webhooks/${id}`, { method: "DELETE" }),

  testWebhook: (id: string) =>
    call<{ delivered: boolean; detail: string; webhook: WebhookRow }>(
      `/api/admin/webhooks/${id}/test`,
      { method: "POST" },
    ),

  // Kasuje odpowiedź, żeby proces zapytał o nią jeszcze raz. To nie „nie
  // dotyczy": tam pytanie zostaje odpowiedziane pominięciem, tutaj wraca.
  clearAnswer: (id: string, key: string, comment: string) =>
    call<{ cleared: string[] }>(`/api/admin/records/${id}/clear`, {
      method: "POST",
      body: JSON.stringify({ key, comment }),
    }),

  // Skan przyniesiony do biura: konsultant wgrywa plik za kandydata.
  attachFile: (id: string, key: string, comment: string, file: File) => {
    const dane = new FormData();
    dane.append("key", key);
    dane.append("comment", comment);
    dane.append("file", file);
    return call<{ key: string; filename: string; cleared: string[] }>(
      `/api/admin/records/${id}/attach`,
      { method: "POST", body: dane },
    );
  },

  // Stan pomocy ręką konsultanta — zwykle „udzielona" po rozmowie.
  setHelp: (id: string, state: string) =>
    call<{ help_status: string; changed: boolean }>(
      `/api/admin/records/${id}/help?state=${state}`,
      { method: "POST" },
    ),

  // Poprawka numeru, na który idą kody SMS; `invite` od razu wysyła link na nowy.
  changePhone: (id: string, phone: string, invite: boolean) =>
    call<{ phone: string; changed: boolean; invited: { sent: boolean; error: string | null } | null }>(
      `/api/admin/records/${id}/phone`,
      { method: "POST", body: JSON.stringify({ phone, invite }) },
    ),

  remind: (id: string) =>
    call<{ sent: boolean; to: string; error: string | null }>(
      `/api/admin/records/${id}/remind`,
      { method: "POST" },
    ),
  // Poprawka konsultanta: stoi nad odczytem z dokumentu, ale ma własne źródło
  // i wymaga powodu — bez niego zostaje po niej sama zmiana wartości.
  override: (id: string, body: { key: string; value: string | null; comment: string }) =>
    call<{ key: string; previous: string | null; value: string; cleared: string[] }>(
      `/api/admin/records/${id}/override`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  resolveHelp: (id: string) =>
    call<{ needs_help: boolean }>(`/api/admin/records/${id}/help/resolve`, { method: "POST" }),
  fileUrl: (recordId: string, fileId: string) => `/api/admin/records/${recordId}/files/${fileId}`,

  autocheck: () => call<AutocheckRun[]>("/api/admin/autocheck"),
  runAutocheck: (checkId: string) =>
    call<AutocheckRun>(`/api/admin/autocheck/${checkId}/run`, { method: "POST" }),

  schemas: () => call<{ schema_id: string; datapoints: number }[]>("/api/admin/schemas"),

  kycTypes: () => call<KycTypes>("/api/admin/kyc-types"),

  createSchema: (schemaId: string, title?: string) =>
    call<{ schema_id: string; datapoints: number }>("/api/admin/schemas", {
      method: "POST",
      body: JSON.stringify({ schema_id: schemaId, title: title || null }),
    }),

  datapoints: (schemaId: string) =>
    call<{
      schema_id: string;
      title: string | null;
      problems: string[];
      /** Etapy tego procesu — wyliczone z węzłów, nie z listy w kodzie. */
      stages: string[];
      /** Nazwy z pierwszego procesu, jako podpowiedź przy zakładaniu nowych. */
      known_stages: string[];
      items: DatapointRow[];
    }>(`/api/admin/schemas/${schemaId}/datapoints`),

  saveDatapoint: (schemaId: string, row: DatapointRow) =>
    call<{ saved: string }>(`/api/admin/schemas/${schemaId}/datapoints/${row.key}`, {
      method: "PUT",
      body: JSON.stringify(row),
    }),

  deleteDatapoint: (schemaId: string, key: string) =>
    call<{ deleted: string }>(`/api/admin/schemas/${schemaId}/datapoints/${key}`, {
      method: "DELETE",
    }),

  // Jedno źródło dokumentacji: plik z repozytorium, nie druga kopia w panelu.
  docs: () => call<{ markdown: string; source: string }>("/api/admin/docs"),

  // Ekran logowania rysuje się z tego, co naprawdę skonfigurowano. Przycisk
  // „Zaloguj przez Microsoft" bez zarejestrowanej aplikacji prowadziłby
  // prosto w błąd po stronie Microsoftu.
  authConfig: () => call<{ microsoft: boolean; key: boolean }>("/api/admin/auth/config"),

  // Treść dokumentów do podpisu. Nazwy pól sprawdza serwer przy zapisie —
  // literówka w placeholderze zostawiłaby puste miejsce w podpisanej umowie.
  templates: (schemaId: string) =>
    call<{
      schema_id: string;
      items: TemplateRow[];
      /** Pola typu `simple_sign` — tylko one mogą mieć własną treść. */
      signature_keys: string[];
      /** Domyślna treść wbudowana per pole — dla edytora podmian. */
      bundled: Record<string, BundledDoc>;
      placeholders: { name: string; description: string }[];
      /** Czy podpis drugiej strony jest już wgrany dla tego procesu. */
      counterparty_signature: boolean;
    }>(`/api/admin/schemas/${schemaId}/templates`),

  /** Podpis drugiej strony (zleceniodawcy) — jeden na proces. `image` = null usuwa. */
  setCounterpartySignature: (schemaId: string, image: string | null) =>
    call<{ set: boolean }>(`/api/admin/schemas/${schemaId}/counterparty-signature`, {
      method: "PUT",
      body: JSON.stringify({ image }),
    }),

  saveTemplate: (schemaId: string, key: string, body: Omit<TemplateRow, "datapoint_key" | "updated_at">) =>
    call<{
      datapoint_key: string;
      /** Treść po przepisaniu nazw ze wzoru zleceniodawcy na nasze. */
      title: Record<string, string>;
      body: Record<string, string>;
      placeholders: string[];
      warnings: string[];
    }>(
      `/api/admin/schemas/${schemaId}/templates/${key}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  deleteTemplate: (schemaId: string, key: string) =>
    call<{ deleted: string }>(`/api/admin/schemas/${schemaId}/templates/${key}`, {
      method: "DELETE",
    }),

  /** Podmiana tekstów dokumentu wbudowanego. Puste pola = zostaw wbudowany;
   *  wyczyszczenie wszystkiego wraca do wbudowanego (`kind === "bundled"`). */
  setTemplateOverride: (schemaId: string, key: string, body: TextOverride) =>
    call<{ datapoint_key: string; kind: "override" | "bundled"; overrides: TextOverride }>(
      `/api/admin/schemas/${schemaId}/templates/${key}/override`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  exportUrl: (schemaId: string) => `/api/admin/schemas/${schemaId}/export`,

  // Cały plik procesu, nie same węzły: bez `templates`/`settings` import gubił
  // treść umowy i mapowania integracji, więc proces wgrany z pliku drukował
  // pustą albo wbudowaną umowę i wywalał się na podpisie.
  importSchema: (
    schemaId: string,
    payload: {
      datapoints: DatapointRow[];
      templates?: unknown;
      settings?: unknown;
      title?: string | null;
    },
  ) =>
    call<{ imported: number; templates: number; warnings: string[] }>("/api/admin/schemas/import", {
      method: "POST",
      body: JSON.stringify({ schema_id: schemaId, replace: true, ...payload }),
    }),

  bulk: (ids: string[], action: "close" | "reset" | "archive") =>
    call<{ updated: number; skipped: string[] }>("/api/admin/records/bulk", {
      method: "POST",
      body: JSON.stringify({ ids, action }),
    }),

  dossierUrl: (id: string) => `/api/admin/records/${id}/dossier.pdf`,

  bulkDossier: (ids: string[]) =>
    saveFile("/api/admin/records/dossier.pdf", `karty-kandydatow-${new Date().toISOString().slice(0, 10)}.pdf`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  brands: () =>
    call<{ items: BrandRow[]; schemas: { schema_id: string; title: string | null }[] }>(
      "/api/admin/brands",
    ),

  // Podgląd liczy serwer — ta sama funkcja, która koloruje proces kandydata.
  deriveBrand: (colors: Record<string, string>) =>
    call<{ tokens: BrandTokens; checks: BrandCheck[]; notes: BrandNote[] }>(
      "/api/admin/brands/derive",
      { method: "POST", body: JSON.stringify({ colors }) },
    ),

  saveBrand: (key: string, body: BrandPayload) =>
    call<BrandRow>(`/api/admin/brands/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteBrand: (key: string) =>
    call<{ deleted: string; restored: boolean }>(`/api/admin/brands/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),

  // Dziennik z bazy, nie kolejka w pamięci procesu: ta znikała przy każdym
  // restarcie i pokazywała treści wiadomości, czyli kody jednorazowe.
  messages: (params: { onlyFailed?: boolean; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.onlyFailed) query.set("only_failed", "true");
    query.set("limit", String(params.limit ?? 100));
    return call<MessageRow[]>(`/api/admin/messages?${query}`);
  },
};
